import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import type { UserPropertiesResponse } from "src/shared/api-types/user";

import Spinner from "src/components/Spinner";

import Show from "../../components/Show";
import EditProperties from "../../components/EditProperties";
import PropertiesList from "../../components/Properties";
import PropertiesService, { DisplayProperty, DisplayRole } from "./service";
import useCrew from "../../hooks/useCrew";
import { getCrewRelations } from "../../api/crew";
import { getUserCompliance } from "../../api/compliance";
import { getRelationTree } from "../../api/courses";
import { getUserProperties, addUserProperty, removeUserProperty } from "../../api/user.deprecated";
import { buildCatalogRequirements, getIncompleteRoles, IncompleteRole } from "../compliance/profileSetup";

interface SetupGap {
  roleName: string;
  incomplete: IncompleteRole;
}

interface MatchParams {
  userId?: string;
  crewId?: string;
}

interface PropertiesEditProps {
  match?: {
    params?: MatchParams;
  };
}

interface PropertyInput {
  roleId?: number;
  positionId?: number;
  operationId?: number;
}

const PropertiesEdit: React.FC<PropertiesEditProps> = ({ match }) => {
  const { t } = useTranslation();
  const { crew } = useCrew();
  const userId = match?.params?.userId ? Number(match.params.userId) : null;
  const crewId: number = Number(match?.params?.crewId ?? crew?.id);

  const [relations, setRelations] = useState<DisplayRole[] | null>(null);
  const [userProperties, setUserProperties] = useState<DisplayProperty[] | null>(null);
  const [setupGaps, setSetupGaps] = useState<SetupGap[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  // Best-effort setup-gaps panel (CREW-530): a fresh compliance calculation
  // plus the crew catalog name exactly what is still missing. Re-runs on every
  // property add/remove via `refresh`, so the list clears as gaps get fixed.
  // Failures leave the panel hidden without affecting the editor itself.
  // The useCache=false call also rewrites this user's server-side compliance
  // cache entry — the members list reads that entry from its cached batch, so
  // its warning flags stay correct without recomputing the whole crew there.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!userId || !crewId || isNaN(crewId)) return;
      try {
        const [complianceResponse, catalogResponse] = await Promise.all([
          getUserCompliance(crewId, userId, false),
          getRelationTree(crewId),
        ]);
        if (cancelled) return;

        const relationTree = complianceResponse.data.relationTree;
        const requirements = buildCatalogRequirements(catalogResponse.tree);
        if (!requirements) {
          setSetupGaps(null);
          return;
        }

        const roleNameById = new Map(relationTree.roles.map((role) => [role.id, role.name]));
        setSetupGaps(
          getIncompleteRoles(relationTree, requirements).map((incomplete) => ({
            roleName: roleNameById.get(incomplete.roleId) ?? String(incomplete.roleId),
            incomplete,
          })),
        );
      } catch {
        if (!cancelled) setSetupGaps(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [crewId, userId, refresh]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const crewRelations = await getCrewRelations(String(crewId), userId ?? undefined);
        const props: UserPropertiesResponse = await getUserProperties(crewId, userId);

        const userProps = PropertiesService.toDisplayObject(props, crewRelations);
        setUserProperties(userProps);

        const displayRelations = PropertiesService.relationsToDisplayObject(crewRelations);
        setRelations(displayRelations);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [crewId, userId, refresh]);

  async function submit(list: PropertyInput[]): Promise<boolean> {
    let needsRefresh = false;
    for (const data of list) {
      const response = await addUserProperty(crewId, userId, data);
      if (response.ok) {
        needsRefresh = true;
      } else {
        return false;
      }
    }
    if (needsRefresh) {
      setRefresh((r) => r + 1);
    }
    return true;
  }

  async function onDelete(data: PropertyInput): Promise<void> {
    const response = await removeUserProperty(crewId, userId, data);
    if (response.ok) {
      setRefresh((r) => r + 1);
    }
  }

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      {!!setupGaps?.length && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            component="div"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("compliance.profile_setup_incomplete_title")}
          </Typography>
          {setupGaps.map(({ roleName, incomplete }) => (
            <Typography key={`gap-${incomplete.roleId}`} variant="body2" component="div">
              <Typography
                variant="body2"
                component="span"
                sx={{
                  fontWeight: 600,
                }}
              >
                {roleName}:{" "}
              </Typography>
              {incomplete.reason === "missing_positions"
                ? t("compliance.profile_setup_missing_positions")
                : t("compliance.profile_setup_missing_operations", {
                    positions: incomplete.positionsMissingOperations.map((position) => position.name).join(", "),
                  })}
            </Typography>
          ))}
        </Alert>
      )}
      <Show r-if={!!relations}>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <EditProperties relations={relations} onSubmit={submit} />
        </Box>
      </Show>
      <Show r-if={!!userProperties}>
        <PropertiesList userProperties={userProperties} onDelete={onDelete} />
      </Show>
    </>
  );
};

export default PropertiesEdit;
